import React, { useCallback, useMemo, useState } from 'react'
import { Tooltip, Typography } from '@mui/material'
import { ReactComponent as FiltersIcon } from '../../../assets/images/Filters.svg'
import { NestedMultiselectFilter, NestedMultiselectFilterItem } from '../NestedMultiselectFilter'
import pluralize from 'pluralize'

type TableColumnFilterItem<TColumnId extends string> = {
    id: TColumnId
    label: string
}

type TableColumnFilterProps<TColumnId extends string> = {
    availableColumns: TableColumnFilterItem<TColumnId>[]
    hiddenColumnIds: TColumnId[]
    /** Columns the table starts without; hiding one of those is the default, not a choice to report. */
    defaultHiddenColumnIds?: TColumnId[]
    onHiddenColumnIdsChange: (hiddenColumnIds: TColumnId[]) => void
}
const tooltipComponentsProps = {
    tooltip: {
        sx: {
            backgroundColor: 'figma.grayscale.contentWrapper',
            px: 2,
            py: 0.5,
            borderRadius: '6px',
            boxShadow: '0px 4px 8px 0px rgba(0, 0, 0, 0.20);',
        },
    },
    arrow: {
        sx: {
            color: 'figma.grayscale.contentWrapper',
        },
    },
}

function TableColumnFilter<TColumnId extends string>({
    availableColumns,
    hiddenColumnIds,
    defaultHiddenColumnIds = [],
    onHiddenColumnIdsChange,
}: TableColumnFilterProps<TColumnId>) {
    const [isTooltipOpen, setIsTooltipOpen] = useState(false)
    const [isPopoverOpen, setIsPopoverOpen] = useState(false)

    const allColumnIds = useMemo(() => availableColumns.map((column) => column.id), [availableColumns])

    const selectedColumnIds = useMemo(
        () => allColumnIds.filter((columnId) => !hiddenColumnIds.includes(columnId)),
        [allColumnIds, hiddenColumnIds]
    )

    const items: NestedMultiselectFilterItem<TColumnId>[] = useMemo(
        () =>
            availableColumns.map((column) => ({
                id: column.id,
                label: column.label,
            })),
        [availableColumns]
    )

    const handleSelectionChange = useCallback(
        (newSelectedColumnIds: TColumnId[]) => {
            onHiddenColumnIdsChange(allColumnIds.filter((columnId) => !newSelectedColumnIds.includes(columnId)))
        },
        [allColumnIds, onHiddenColumnIdsChange]
    )

    const hiddenColumnsCount = useMemo(
        () => hiddenColumnIds.filter((columnId) => !defaultHiddenColumnIds.includes(columnId)).length,
        [defaultHiddenColumnIds, hiddenColumnIds]
    )

    const canReset = useMemo(
        () =>
            hiddenColumnIds.length !== defaultHiddenColumnIds.length ||
            hiddenColumnIds.some((columnId) => !defaultHiddenColumnIds.includes(columnId)),
        [defaultHiddenColumnIds, hiddenColumnIds]
    )

    const handleReset = useCallback(
        () => onHiddenColumnIdsChange(defaultHiddenColumnIds),
        [defaultHiddenColumnIds, onHiddenColumnIdsChange]
    )

    return (
        <Tooltip
            placement="top-start"
            arrow
            open={isTooltipOpen && !isPopoverOpen}
            onOpen={() => {
                if (!isPopoverOpen) {
                    setIsTooltipOpen(true)
                }
            }}
            onClose={() => setIsTooltipOpen(false)}
            disableHoverListener={isPopoverOpen}
            title={
                <Typography
                    variant={hiddenColumnsCount ? '14x600' : '14x400'}
                    color={hiddenColumnsCount ? 'figma.primary' : 'figma.grayscale.textHier1'}
                >
                    {hiddenColumnsCount
                        ? `${hiddenColumnsCount} ${pluralize('column', hiddenColumnsCount)} hidden`
                        : 'Apply filters'}
                </Typography>
            }
            componentsProps={tooltipComponentsProps}
        >
            <span onClick={() => setIsTooltipOpen(false)}>
                <NestedMultiselectFilter
                    icon={FiltersIcon}
                    items={items}
                    selectedItemIds={selectedColumnIds}
                    onSelectionChange={handleSelectionChange}
                    onReset={handleReset}
                    canReset={canReset}
                    onOpenChange={(open) => {
                        setIsPopoverOpen(open)
                        if (open) {
                            setIsTooltipOpen(false)
                        }
                    }}
                />
            </span>
        </Tooltip>
    )
}

export default TableColumnFilter
